import React, { FC } from "react";
import { Controller, useFormContext, useFieldArray } from "react-hook-form";
import TextField from "@mui/material/TextField";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import { useTranslation } from "react-i18next";
import Grid from "@mui/material/Grid2";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import { CreateProgramFormData } from "./validation.schema";
import { FormSection } from "@/ui/sections/FormSection";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";

type TabProgramGeofenceProps = {
  editMode?: boolean;
};

export const TabProgramGeofence: FC<TabProgramGeofenceProps> = ({
  editMode = false,
}) => {
  const { t } = useTranslation("program");

  const {
    control,
    formState: { errors },
  } = useFormContext<CreateProgramFormData>();

  // Field array for onDemandValidationDays
  // Use the same control and errors as the rest of the form
  // control and errors already declared above, do not redeclare
  const {
    fields: onDemandDaysFields,
    append: appendOnDemandDay,
    remove: removeOnDemandDay,
  } = useFieldArray({
    control,
    name: "onDemandValidationDays" as never,
  });

  return (
    <Grid container spacing={2}>
      <Grid size={{ xs: 12, md: 6 }}>
        <FormSection title={t("sections.geofence-parameters")}>
          <Controller
            name="minutesPingGeofenceValidationRequest"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                {...field}
                id="minutes-ping-geofence-validation-request"
                label={t("labels.minutes-ping-geofence-validation-request")}
                variant="outlined"
                value={field.value}
                onChange={(value) => field.onChange(value)}
                error={!!errors.minutesPingGeofenceValidationRequest}
                helperText={
                  errors.minutesPingGeofenceValidationRequest
                    ? errors.minutesPingGeofenceValidationRequest.message
                    : null
                }
                fullWidth
                type="number"
              />
            )}
          />
          <Controller
            name="metersGeofenceAccuracyThreshold"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                {...field}
                id="meters-geofence-accuracy-threshold"
                label={t("labels.meters-geofence-accuracy-threshold")}
                variant="outlined"
                value={field.value}
                onChange={(value) => field.onChange(value)}
                error={!!errors.metersGeofenceAccuracyThreshold}
                helperText={
                  errors.metersGeofenceAccuracyThreshold
                    ? errors.metersGeofenceAccuracyThreshold.message
                    : null
                }
                fullWidth
                type="number"
              />
            )}
          />
          <Controller
            name="metersRadiusGeofenceRequestValidation"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                {...field}
                id="meters-radius-geofence-request-validation"
                label={t("labels.meters-radius-geofence-request-validation")}
                variant="outlined"
                value={field.value}
                onChange={(value) => field.onChange(value)}
                error={!!errors.metersRadiusGeofenceRequestValidation}
                helperText={
                  errors.metersRadiusGeofenceRequestValidation
                    ? errors.metersRadiusGeofenceRequestValidation.message
                    : null
                }
                fullWidth
                type="number"
              />
            )}
          />
          <Controller
            name="requestGeofenceValidationsPerDay"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                {...field}
                id="request-geofence-validations-per-day"
                label={t("labels.request-geofence-validations-per-day")}
                variant="outlined"
                value={field.value}
                onChange={(value) => field.onChange(value)}
                error={!!errors.requestGeofenceValidationsPerDay}
                helperText={
                  errors.requestGeofenceValidationsPerDay
                    ? errors.requestGeofenceValidationsPerDay.message
                    : null
                }
                fullWidth
                type="number"
              />
            )}
          />
          <Controller
            name="minimumMinsGeofenceBetweenRequestValidations"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                {...field}
                id="minimum-mins-geofence-between-request-validations"
                label={t(
                  "labels.minimum-mins-geofence-between-request-validations",
                )}
                variant="outlined"
                value={field.value}
                onChange={(value) => field.onChange(value)}
                error={!!errors.minimumMinsGeofenceBetweenRequestValidations}
                helperText={
                  errors.minimumMinsGeofenceBetweenRequestValidations
                    ? errors.minimumMinsGeofenceBetweenRequestValidations
                        .message
                    : null
                }
                fullWidth
                type="number"
              />
            )}
          />
          <Controller
            name="minutesPingGeofenceAlive"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                {...field}
                id="minutes-ping-geofence-alive"
                label={t("labels.minutes-ping-geofence-alive")}
                variant="outlined"
                value={field.value}
                onChange={(value) => field.onChange(value)}
                error={!!errors.minutesPingGeofenceAlive}
                helperText={
                  errors.minutesPingGeofenceAlive
                    ? errors.minutesPingGeofenceAlive.message
                    : null
                }
                fullWidth
                type="number"
              />
            )}
          />
        </FormSection>
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <FormSection
          title={t(
            "sections.validation-request-settings",
            "Validation Request Settings",
          )}
        >
          <Controller
            name="minutesValidationTimer"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                {...field}
                id="minutes-validation-timer"
                label={t(
                  "labels.minutes-validation-timer",
                  "Minutes Validation Timer",
                )}
                variant="outlined"
                value={field.value}
                onChange={(value) => field.onChange(value)}
                error={!!errors.minutesValidationTimer}
                helperText={
                  errors.minutesValidationTimer
                    ? errors.minutesValidationTimer.message
                    : null
                }
                fullWidth
                type="number"
              />
            )}
          />
          <Controller
            name="timerValidationRequestOpened"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                {...field}
                id="timer-validation-request-opened"
                label={t(
                  "labels.timer-validation-request-opened",
                  "Timer Validation Request Opened",
                )}
                variant="outlined"
                value={field.value}
                onChange={(value) => field.onChange(value)}
                error={!!errors.timerValidationRequestOpened}
                helperText={
                  errors.timerValidationRequestOpened
                    ? errors.timerValidationRequestOpened.message
                    : null
                }
                fullWidth
                type="number"
              />
            )}
          />
          <Controller
            name="typeRequestValidation"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                {...field}
                id="type-request-validation"
                label={t(
                  "labels.type-request-validation",
                  "Type Request Validation",
                )}
                variant="outlined"
                value={field.value}
                onChange={(value) => field.onChange(value)}
                error={!!errors.typeRequestValidation}
                helperText={
                  errors.typeRequestValidation
                    ? errors.typeRequestValidation.message
                    : null
                }
                fullWidth
                select
              >
                <MenuItem value="">Select</MenuItem>
                <MenuItem value="GEOFENCE">GEOFENCE</MenuItem>
                <MenuItem value="GEOFENCE_AND_TIME">GEOFENCE_AND_TIME</MenuItem>
              </TextField>
            )}
          />
          <Controller
            name="distanceFilterGeofenceRequestValidation"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                {...field}
                id="distance-filter-geofence-request-validation"
                label={t(
                  "labels.distance-filter-geofence-request-validation",
                  "Distance Filter Geofence",
                )}
                variant="outlined"
                value={field.value}
                onChange={(value) => field.onChange(value)}
                error={!!errors.distanceFilterGeofenceRequestValidation}
                helperText={
                  errors.distanceFilterGeofenceRequestValidation
                    ? errors.distanceFilterGeofenceRequestValidation.message
                    : null
                }
                fullWidth
                type="number"
              />
            )}
          />
          <Controller
            name="desiredAccuracy"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                {...field}
                id="desired-accuracy"
                label={t("labels.desired-accuracy", "Desired Accuracy")}
                variant="outlined"
                value={field.value}
                onChange={(value) => field.onChange(value)}
                error={!!errors.desiredAccuracy}
                helperText={
                  errors.desiredAccuracy ? errors.desiredAccuracy.message : null
                }
                fullWidth
                select
              >
                {/*
                  COYN-1347: store the short SDK form ("HIGH", "MEDIUM", …)
                  in the database so the mobile switch in lib/geofence.ts
                  matches it directly. Labels keep the long enum so the
                  operator UI looks unchanged.
                */}
                <MenuItem value="">Select</MenuItem>
                <MenuItem value="NAVIGATION">
                  DESIRED_ACCURACY_NAVIGATION
                </MenuItem>
                <MenuItem value="HIGH">DESIRED_ACCURACY_HIGH</MenuItem>
                <MenuItem value="MEDIUM">DESIRED_ACCURACY_MEDIUM</MenuItem>
                <MenuItem value="LOW">DESIRED_ACCURACY_LOW</MenuItem>
              </TextField>
            )}
          />
          <Controller
            name="showGeofenceChip"
            control={control}
            defaultValue={false}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Switch
                    checked={!!field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                  />
                }
                label={t("labels.show-geofence-chip", "Show Geofence Chip")}
              />
            )}
          />
          <Controller
            name="automaticGeofenceTrigger"
            control={control}
            defaultValue={false}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Switch
                    checked={!!field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                  />
                }
                label={t(
                  "labels.automatic-geofence-trigger",
                  "Automatic Geofence Trigger",
                )}
              />
            )}
          />
          <Controller
            name="minutesTimeToCompleteValidation"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                {...field}
                id="minutes-time-to-complete-validation"
                label={t(
                  "labels.minutes-time-to-complete-validation",
                  "Minutes to Complete Validation",
                )}
                variant="outlined"
                value={field.value}
                onChange={(value) => field.onChange(value)}
                error={!!errors.minutesTimeToCompleteValidation}
                helperText={
                  errors.minutesTimeToCompleteValidation
                    ? errors.minutesTimeToCompleteValidation.message
                    : null
                }
                fullWidth
                type="number"
              />
            )}
          />
          <Controller
            name="validationRequestMaxAttempts"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                {...field}
                id="validation-request-max-attempts"
                label={t(
                  "labels.validation-request-max-attempts",
                  "Validation Request Max Attempts",
                )}
                variant="outlined"
                value={field.value}
                onChange={(value) => field.onChange(value)}
                error={!!errors.validationRequestMaxAttempts}
                helperText={
                  errors.validationRequestMaxAttempts
                    ? errors.validationRequestMaxAttempts.message
                    : null
                }
                fullWidth
                type="number"
              />
            )}
          />
          <Controller
            name="enabledRewardFirstValidationSuccess"
            control={control}
            defaultValue={false}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Switch
                    checked={!!field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                  />
                }
                label={t(
                  "labels.enabled-reward-first-validation-success",
                  "Enable Reward First Validation Success",
                )}
              />
            )}
          />
          <Controller
            name="enabledRewardValidationRequestSuccess"
            control={control}
            defaultValue={false}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Switch
                    checked={!!field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                  />
                }
                label={t(
                  "labels.enabled-reward-validation-request-success",
                  "Enable Reward Validation Request Success",
                )}
              />
            )}
          />
          <Controller
            name="enabledRewardOnDemandValidationSuccess"
            control={control}
            defaultValue={false}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Switch
                    checked={!!field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                  />
                }
                label={t(
                  "labels.enabled-reward-on-demand-validation-success",
                  "Enable Reward On-Demand Validation Success",
                )}
              />
            )}
          />
          <Controller
            name="enableConsiderTimerOnMissedExpiration"
            control={control}
            defaultValue={true}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Switch
                    checked={!!field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                  />
                }
                label={t(
                  "labels.enable-consider-timer-on-missed-expiration",
                  "Enable Consider Timer On Missed Expiration",
                )}
              />
            )}
          />
          <Controller
            name="enabledRewardNonCarriage"
            control={control}
            defaultValue={false}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Switch
                    checked={!!field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                  />
                }
                label={t(
                  "labels.enabled-reward-non-carriage",
                  "Enable Reward Non-Carriage",
                )}
              />
            )}
          />
          <Controller
            name="onDemandValidationRequest"
            control={control}
            defaultValue={false}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Switch
                    checked={!!field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                  />
                }
                label={t(
                  "labels.on-demand-validation-request",
                  "On-Demand Validation Request",
                )}
              />
            )}
          />
          <Controller
            name="onDemandValidationPeriodDays"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                {...field}
                id="on-demand-validation-period-days"
                label={t(
                  "labels.on-demand-validation-period-days",
                  "On-Demand Validation Period Days",
                )}
                variant="outlined"
                value={field.value}
                onChange={(value) => field.onChange(value)}
                error={!!errors.onDemandValidationPeriodDays}
                helperText={
                  errors.onDemandValidationPeriodDays
                    ? errors.onDemandValidationPeriodDays.message
                    : null
                }
                fullWidth
                type="number"
              />
            )}
          />
          <FormSection
            title={t(
              "sections.on-demand-validation-days",
              "On-Demand Validation Days",
            )}
            tooltip={t(
              "tooltips.on-demand-validation-days",
              "Add days for on-demand validation (as numbers).",
            )}
          >
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {onDemandDaysFields.map((item, index) => (
                <Box
                  key={item.id}
                  sx={{ display: "flex", gap: 2, alignItems: "center" }}
                >
                  <Controller
                    name={`onDemandValidationDays.${index}` as const}
                    control={control}
                    // defaultValue not needed, useFieldArray provides value
                    render={({ field }) => (
                      <TextField
                        {...field}
                        id={`on-demand-validation-day-${index}`}
                        label={t("labels.on-demand-validation-day", "Day")}
                        variant="outlined"
                        value={field.value ?? ""}
                        onChange={(e) => field.onChange(Number(e.target.value))}
                        error={
                          Array.isArray(errors.onDemandValidationDays) &&
                          !!errors.onDemandValidationDays[index]
                        }
                        helperText={
                          Array.isArray(errors.onDemandValidationDays) &&
                          errors.onDemandValidationDays[index]?.message
                        }
                        type="number"
                        sx={{ minWidth: 120 }}
                        required
                      />
                    )}
                  />
                  <IconButton
                    onClick={() => removeOnDemandDay(index)}
                    color="error"
                    size="small"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              ))}
              <Button
                startIcon={<AddIcon />}
                onClick={() => appendOnDemandDay(0)}
                variant="outlined"
                size="small"
              >
                {t("buttons.add-on-demand-validation-day", "Add Day")}
              </Button>
            </Box>
          </FormSection>
          <Controller
            name="stickyAndroidNotificationTitle"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                {...field}
                id="sticky-android-notification-title"
                label={t(
                  "labels.sticky-android-notification-title",
                  "Sticky Android Notification Title",
                )}
                variant="outlined"
                value={field.value}
                onChange={(value) => field.onChange(value)}
                error={!!errors.stickyAndroidNotificationTitle}
                helperText={
                  errors.stickyAndroidNotificationTitle
                    ? errors.stickyAndroidNotificationTitle.message
                    : null
                }
                fullWidth
              />
            )}
          />
          <Controller
            name="stickyAndroidNotificationText"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                {...field}
                id="sticky-android-notification-text"
                label={t(
                  "labels.sticky-android-notification-text",
                  "Sticky Android Notification Text",
                )}
                variant="outlined"
                value={field.value}
                onChange={(value) => field.onChange(value)}
                error={!!errors.stickyAndroidNotificationText}
                helperText={
                  errors.stickyAndroidNotificationText
                    ? errors.stickyAndroidNotificationText.message
                    : null
                }
                fullWidth
                multiline
                rows={3}
              />
            )}
          />
        </FormSection>
      </Grid>
      {/* Geofence & Validation: On-Demand Validation Days */}
    </Grid>
  );
};
